/**
 * Edit Basic Information Modal
 *
 * Modal for editing vendor basic information including:
 * - Contact Details (Phone, Email, Website, Fax)
 * - Registered Location (Country, Postcode, single Address textfield)
 * - Notes
 *
 * board2-#79 「地址格式改成一個textfield，丟到googlet拆分」
 * (page 2cb89adb-b34d-80ee-a729-d094a38c45cc): the registered address is
 * entered as country + postcode + ONE full-address textfield. State/city
 * Selects are removed — stateId/cityId are backend-parse OUTPUTS (Google
 * Maps split, simulated by the mocks); they remain in the form data only as
 * hidden passthrough so the detail-page save contract keeps compiling.
 *
 * Uses React Hook Form + matches create vendor wizard styling (Step 1)
 */

import { useEffect, useMemo } from 'react';
import { Modal, Input, Select, Row, Col, Typography, theme, Tag } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { useCountriesData } from '@/hooks/useLocations';
import { usePostcodeLookup } from '@/hooks/usePostcodeLookup';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import type { VendorDetailResponse } from '@/types/api';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import { getCountryCodeOptions, DEFAULT_COUNTRY_CODE } from '@/utils/phoneUtils';
import {
  editBasicInfoFormSchema,
  type EditBasicInfoFormData,
} from './editBasicInfoFormSchema';

const { Text } = Typography;

export interface EditBasicInfoModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: EditBasicInfoFormData) => Promise<void>;
  vendor: VendorDetailResponse | null;
  loading?: boolean;
}


export const EditBasicInfoModal: React.FC<EditBasicInfoModalProps> = ({
  open,
  onCancel,
  onSave,
  vendor,
  loading = false,
}) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t } = useVendorTranslation();
  const { handleError, handleSuccess } = useErrorHandler();

  // Mock sales representatives data (TODO: Replace with API call)
  const salesRepresentatives = [
    { userId: 1, name: 'John Smith' },
    { userId: 2, name: 'Sarah Johnson' },
    { userId: 3, name: 'Michael Chen' },
    { userId: 4, name: 'Emily Davis' },
    { userId: 5, name: 'David Wilson' },
  ];

  // Compute form values from vendor data (memoized to prevent unnecessary re-renders)
  const formValues = useMemo((): EditBasicInfoFormData => {
    if (!vendor) {
      return {
        phoneCountryCode: DEFAULT_COUNTRY_CODE,
        phoneNumber: '',
        phoneExtension: '',
        email: '',
        web: '',
        fax: '',
        countryId: null,
        stateId: null,
        stateName: '',
        cityId: null,
        cityName: '',
        postcode: '',
        address: '',
        salesRepresentativeIds: [],
        primaryPort: '',
      };
    }

    return {
      // Use phoneCountryCode directly from API
      phoneCountryCode: vendor.phoneCountryCode || DEFAULT_COUNTRY_CODE,
      phoneNumber: vendor.phoneNumber || '',
      phoneExtension: vendor.phoneExtension || '',
      email: vendor.email || '',
      web: vendor.web || '',
      fax: vendor.fax || '',
      countryId: vendor.countryId || null,
      // stateId/cityId: stored backend-parse outputs ride along untouched
      stateId: vendor.stateId || null,
      stateName: '',
      cityId: vendor.cityId || null,
      cityName: '',
      postcode: vendor.postcode || '',
      address: vendor.address || '',
      salesRepresentativeIds: vendor.salesRepresentativeIds || [],
      primaryPort: vendor.primaryPort || '',
    };
  }, [vendor]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<EditBasicInfoFormData>({
    resolver: zodResolver(editBasicInfoFormSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    // Use `values` prop for reactive updates from external data (recommended by RHF docs)
    values: formValues,
  });

  // Reset form when modal opens to ensure fresh data from API
  useEffect(() => {
    if (open && formValues) {
      reset(formValues);
    }
  }, [open, formValues, reset]);

  // Countries for the country Select — use vendor's addressLang so names show
  // in the language the address was entered in
  const { countries, isLoading: isLoadingCountries } = useCountriesData(vendor?.addressLang);

  // Resolve countryCode from the watched countryId for the postcode lookup
  const watchedCountryId = watch('countryId');
  const selectedCountryCode = useMemo(() => {
    if (!watchedCountryId || !countries) return undefined;
    return countries.find((c) => c.countryId === watchedCountryId)?.countryCode;
  }, [watchedCountryId, countries]);

  // Postcode lookup hook — pre-resolves the state/city PARSE OUTPUTS when the
  // postcode is on the seeded dataset (board2-#79: entry stays country +
  // postcode + address; the backend re-parses unresolved rows on save)
  const { handlePostcodeChange, handlePostcodeBlur } = usePostcodeLookup({
    countryCode: selectedCountryCode,
    setValue: setValue as (name: string, value: unknown) => void,
    stateIdField: 'stateId',
    stateLabelField: 'stateName',
    cityIdField: 'cityId',
    cityLabelField: 'cityName',
  });

  const handleFormSubmit = async (data: EditBasicInfoFormData) => {
    try {
      await onSave(data);
      handleSuccess(t('vendor.messages.basicInfoUpdatedSuccess'));
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  // Override formStyles with generous spacing (matching create vendor wizard Step 1)
  const fieldContainerStyle = {
    ...formStyles.fieldContainerStyle,
    marginBottom: '32px',
    padding: '20px',
  };

  // Use standardized styles from formStyles
  const sectionTitleStyle = formStyles.sectionTitleStyle;
  const labelStyle = formStyles.labelStyle;

  return (
    <>
      {/* Global styles for dropdown items to match input height */}
      <style>
        {`
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <Modal
        title={t('vendor.modals.editBasicInformation')}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(handleFormSubmit)}
        okText={t('vendor.actions.save')}
        cancelText={t('vendor.actions.cancel')}
        confirmLoading={isSubmitting || loading}
        width={800}
        okButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        cancelButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
      >
        <form>
        {/* Contact Details Section */}
        <div style={fieldContainerStyle}>
          <Text strong style={sectionTitleStyle}>
            {t('vendor.sections.contactDetails')}
          </Text>

          {/* Row 1: Country Code + Phone Number + Extension */}
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.phoneCountryCode')}
              </Text>
              <Controller
                name="phoneCountryCode"
                control={control}
                rules={{ required: t('vendor.validation.countryCodeRequired') }}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.countryCodePlaceholder')}
                      showSearch
                      options={getCountryCodeOptions(t)}
                      style={{
                        ...formStyles.inputStyle,
                                                borderColor: errors.phoneCountryCode ? token.colorError : undefined,
                      }}
                    />
                    {errors.phoneCountryCode && touchedFields.phoneCountryCode && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.phoneCountryCode.message!)}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>

            <Col span={14}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.phoneNumberLabel')}
              </Text>
              <Controller
                name="phoneNumber"
                control={control}
                rules={{ required: t('vendor.validation.phoneRequired') }}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.samplePhoneNumber')}
                      onChange={(e) => {
                        // Only allow numeric characters
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        field.onChange(numericValue);
                      }}
                      style={{
                        ...formStyles.inputStyle,
                                                borderColor: errors.phoneNumber ? token.colorError : undefined,
                      }}
                    />
                    {errors.phoneNumber && touchedFields.phoneNumber && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.phoneNumber.message!)}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>

            <Col span={4}>
              <Text style={labelStyle}>{t('vendor.fields.ext')}</Text>
              <Controller
                name="phoneExtension"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    placeholder={t('vendor.placeholders.ext')}
                    onChange={(e) => {
                      // Only allow numeric characters
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      field.onChange(numericValue);
                    }}
                    style={{ ...formStyles.inputStyle, }}
                  />
                )}
              />
            </Col>
          </Row>

          {/* Row 2: Email (full width) */}
          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            <Col span={24}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.email')}
              </Text>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: t('vendor.validation.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('vendor.validation.invalidEmail'),
                  },
                }}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      size="large"
                      type="email"
                      placeholder={t('vendor.placeholders.sampleEmail')}
                      style={{
                        ...formStyles.inputStyle,
                                                borderColor: errors.email ? token.colorError : undefined,
                      }}
                    />
                    {errors.email && touchedFields.email && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.email.message!)}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>

          {/* Row 3: Web + Fax */}
          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            <Col span={12}>
              <Text style={labelStyle}>{t('vendor.fields.website')}</Text>
              <Controller
                name="web"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    placeholder={t('vendor.placeholders.sampleWebsite')}
                    style={{ ...formStyles.inputStyle, }}
                  />
                )}
              />
            </Col>

            <Col span={12}>
              <Text style={labelStyle}>{t('vendor.fields.fax')}</Text>
              <Controller
                name="fax"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    placeholder={t('vendor.placeholders.faxNumber')}
                    style={{ ...formStyles.inputStyle, }}
                  />
                )}
              />
            </Col>
          </Row>
        </div>

        {/* Registered Location Section — board2-#79 single-address entry:
            country + postcode + ONE full-address textfield. State/city are
            backend-parse outputs (no Selects). */}
        <div style={fieldContainerStyle}>
          <Text strong style={sectionTitleStyle}>
            {t('vendor.sections.location')}
          </Text>

          {/* Row 1: Country + Postcode */}
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.country')}
              </Text>
              <Controller
                name="countryId"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      value={field.value ?? undefined}
                      size="large"
                      showSearch
                      placeholder={t('vendor.placeholders.country')}
                      loading={isLoadingCountries}
                      optionFilterProp="label"
                      options={countries?.map((country) => ({
                        label: country.countryName,
                        value: country.countryId,
                      }))}
                      onChange={(value) => {
                        field.onChange(value ?? null);
                        field.onBlur(); // Mark as touched (Select has no natural blur)
                        // A country change invalidates any stored parse split —
                        // clear the stale parse outputs; the backend re-parses on save
                        setValue('stateId', null);
                        setValue('stateName', '');
                        setValue('cityId', null);
                        setValue('cityName', '');
                      }}
                      status={errors.countryId ? 'error' : undefined}
                      style={formStyles.inputStyle}
                    />
                    {errors.countryId && touchedFields.countryId && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.countryId.message ?? '')}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>

            <Col span={12}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.postcode')}
              </Text>
              <Controller
                name="postcode"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.samplePostcode')}
                      onChange={(e) => {
                        field.onChange(e);
                        handlePostcodeChange(e.target.value);
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        handlePostcodeBlur(e.target.value);
                      }}
                      style={{
                        ...formStyles.inputStyle,
                                                borderColor: errors.postcode ? token.colorError : undefined,
                      }}
                    />
                    {errors.postcode && touchedFields.postcode && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.postcode.message ?? '')}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>

          {/* Row 2: single full-address textfield (whole street address in
              ONE field; the backend splits it into stateId/cityId) */}
          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            <Col span={24}>
              <Text style={labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.physicalAddress')}
              </Text>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('vendor.placeholders.physicalAddress')}
                      style={{
                        ...formStyles.inputStyle,
                                                borderColor: errors.address ? token.colorError : undefined,
                      }}
                    />
                    {errors.address && touchedFields.address && (
                      <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.address.message ?? '')}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
        </div>

        {/* Additional Information Section */}
        <div style={fieldContainerStyle}>
          <Text strong style={sectionTitleStyle}>
            {t('vendor.sections.additional')}
          </Text>

          {/* Row 1: Sales Rep + Primary Port */}
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text style={labelStyle}>{t('vendor.fields.salesRep')}</Text>
              <Controller
                name="salesRepresentativeIds"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    mode="multiple"
                    size="large"
                    placeholder={t('vendor.placeholders.salesRep')}
                    showSearch
                    allowClear
                    maxTagCount="responsive"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                      // Convert undefined to empty array when clearing
                      const safeValue = value ?? [];
                      // Enforce maximum 2 selections
                      if (safeValue.length <= 2) {
                        field.onChange(safeValue);
                      }
                    }}
                    options={salesRepresentatives.map((rep) => ({
                      label: rep.name,
                      value: rep.userId,
                      disabled:
                        (field.value?.length ?? 0) >= 2 &&
                        !field.value?.includes(rep.userId),
                    }))}
                    tagRender={(props) => {
                      const rep = salesRepresentatives.find(
                        (r) => r.userId === props.value
                      );
                      return (
                        <Tag
                          closable={props.closable}
                          onClose={props.onClose}
                          style={{
                            margin: '2px 4px 2px 0',
                            padding: '0px 8px',
                            fontSize: token.fontSizeSM,
                            lineHeight: '24px',
                            borderRadius: `${radiusTokens.control}px`,
                            backgroundColor: token.colorPrimaryBg,
                            color: token.colorPrimary,
                            border: `1px solid ${token.colorPrimaryBorder}`,
                          }}
                        >
                          {rep?.name}
                        </Tag>
                      );
                    }}
                    style={{ ...formStyles.inputStyle, }}
                  />
                )}
              />
            </Col>
            <Col span={12}>
              <Text style={labelStyle}>{t('vendor.fields.primaryPort')}</Text>
              <Controller
                name="primaryPort"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    size="large"
                    placeholder={t('vendor.placeholders.primaryPort')}
                    showSearch
                    allowClear
                    onChange={(value) => field.onChange(value ?? null)}
                    options={[
                      { label: t('vendor.ports.losAngeles'), value: 'USLA' },
                      { label: t('vendor.ports.longBeach'), value: 'USLB' },
                      { label: t('vendor.ports.shanghai'), value: 'CNSHA' },
                      { label: t('vendor.ports.singapore'), value: 'SGSIN' },
                      { label: t('vendor.ports.shenzhen'), value: 'CNSZX' },
                      { label: t('vendor.ports.rotterdam'), value: 'NLRTM' },
                    ]}
                    style={{ ...formStyles.inputStyle, }}
                  />
                )}
              />
            </Col>
          </Row>

        </div>

      </form>
    </Modal>
    </>
  );
};

export default EditBasicInfoModal;
